import React, { useState, useEffect, useMemo } from 'react';
import { coveApi, customersApi } from '../services/api';
import { CoveDevice, CoveStatus, CoveCategoryMeta, Customer } from '../types';
import toast from 'react-hot-toast';
import './Cove.css';

const FALLBACK_CATEGORIES: Record<string, CoveCategoryMeta> = {
  server_physical: { label: 'Windows Server (Physical)', rate: '750.00', unit: 'server' },
  server_vm: { label: 'Server (Virtual Machine)', rate: '325.00', unit: 'server' },
  desktop: { label: 'Desktop OS', rate: '105.00', unit: 'device' },
  m365: { label: 'Microsoft 365 Backup', rate: '55.00', unit: 'mailbox' },
};

const CATEGORY_ORDER = ['server_physical', 'server_vm', 'desktop', 'm365'];

const SHORT_LABEL: Record<string, string> = {
  server_physical: 'Server · Physical',
  server_vm: 'Server · VM',
  desktop: 'Desktop',
  m365: 'M365',
};

const rand = (n: number) =>
  `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type CatFilter = 'all' | 'server' | 'desktop' | 'm365';
type MapFilter = 'all' | 'mapped' | 'unmapped';

function CoveBackups() {
  const [status, setStatus] = useState<CoveStatus | null>(null);
  const [devices, setDevices] = useState<CoveDevice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [catFilter, setCatFilter] = useState<CatFilter>('all');
  const [mapFilter, setMapFilter] = useState<MapFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedDevices, setSelectedDevices] = useState<Set<number>>(new Set());

  // Map / edit modal
  const [mapDevice, setMapDevice] = useState<CoveDevice | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [mapCategory, setMapCategory] = useState<string>('server_vm');
  const [mapPrice, setMapPrice] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Bulk modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState<string>('');
  const [bulkPrice, setBulkPrice] = useState<string>('');

  const [config, setConfig] = useState({ partner: '', username: '', api_password: '', user_password: '' });

  const categories: Record<string, CoveCategoryMeta> = useMemo(
    () => status?.categories ?? FALLBACK_CATEGORIES,
    [status]
  );
  const rate = (cat: string) => categories[cat]?.rate ?? FALLBACK_CATEGORIES[cat]?.rate ?? '0.00';
  const label = (cat: string) => categories[cat]?.label ?? cat;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusData, customerData] = await Promise.all([
        coveApi.getStatus().catch(() => ({ connected: false, error: 'Not configured' } as CoveStatus)),
        customersApi.list(),
      ]);
      setStatus(statusData);
      setCustomers(customerData);
      if (statusData.connected) {
        setDevices(await coveApi.getDevices());
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fast reload after a write — Cove device list is cached server-side, billing recomputed from DB
  const reloadDevices = async () => {
    try {
      setDevices(await coveApi.getDevices(false));
    } catch {
      toast.error('Failed to reload devices');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setDevices(await coveApi.getDevices(true));
      toast.success('Refreshed from Cove');
    } catch {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.partner || !config.username || !config.api_password || !config.user_password) {
      toast.error('All fields are required');
      return;
    }
    try {
      const result = await coveApi.saveSettings(config);
      setStatus(result);
      if (result.connected) {
        toast.success('Connected to Cove');
        setShowConfig(false);
        setConfig({ ...config, user_password: '', api_password: '' });
        loadData();
      } else {
        toast.error(result.error || 'Connection failed');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await coveApi.sync();
      toast.success(`Synced: ${result.matched} matched, ${result.created_mappings} new mappings`);
      reloadDevices();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openMapModal = (device: CoveDevice) => {
    setMapDevice(device);
    setSelectedCustomer(device.matched_customer_id);
    setCustomerSearch('');
    const cat = device.billing?.category ?? device.category;
    setMapCategory(cat);
    setMapPrice(device.billing?.unit_price ?? rate(cat));
  };

  const changeMapCategory = (cat: string) => {
    setMapCategory(cat);
    setMapPrice(rate(cat)); // price follows category default; user can tweak
  };

  const handleMap = async () => {
    if (!mapDevice || !selectedCustomer) {
      toast.error('Select a customer');
      return;
    }
    setSaving(true);
    try {
      await coveApi.mapDevice(mapDevice, selectedCustomer, { category: mapCategory, unitPrice: mapPrice });
      toast.success('Device mapped');
      setMapDevice(null);
      reloadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to map device');
    } finally {
      setSaving(false);
    }
  };

  const handleInlineCategory = async (device: CoveDevice, newCat: string) => {
    try {
      await coveApi.updatePricing(device.id, {
        category: newCat,
        unitPrice: rate(newCat),
        mailboxCount: device.mailbox_count,
      });
      toast.success('Type updated');
      reloadDevices();
    } catch {
      toast.error('Failed to update type');
    }
  };

  const handleUnmap = async (deviceId: number) => {
    try {
      await coveApi.unmapDevice(deviceId);
      toast.success('Mapping removed');
      reloadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove mapping');
    }
  };

  const toggleDevice = (id: number) => {
    const next = new Set(selectedDevices);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedDevices(next);
  };

  const handleBulkMap = async () => {
    if (selectedDevices.size === 0 || !selectedCustomer) {
      toast.error('Select devices and a customer');
      return;
    }
    setSaving(true);
    try {
      const toMap = devices.filter(d => selectedDevices.has(d.id));
      const result = await coveApi.bulkMapDevices(toMap, selectedCustomer, {
        category: bulkCategory || undefined,
        unitPrice: bulkPrice || undefined,
      });
      toast.success(result.message);
      setBulkOpen(false);
      setSelectedDevices(new Set());
      setSelectedCustomer(null);
      setBulkCategory('');
      setBulkPrice('');
      reloadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to map devices');
    } finally {
      setSaving(false);
    }
  };

  // ── Derived ──
  const isServer = (c: string) => c === 'server_physical' || c === 'server_vm';

  const filteredDevices = useMemo(() => {
    const q = search.toLowerCase();
    return devices.filter(d => {
      const cat = d.billing?.category ?? d.category;
      const catOk =
        catFilter === 'all' ||
        (catFilter === 'server' && isServer(cat)) ||
        (catFilter === 'desktop' && cat === 'desktop') ||
        (catFilter === 'm365' && cat === 'm365');
      const mapOk =
        mapFilter === 'all' ||
        (mapFilter === 'mapped' && d.matched_customer_id) ||
        (mapFilter === 'unmapped' && !d.matched_customer_id);
      const searchOk =
        !q ||
        d.name.toLowerCase().includes(q) ||
        (d.partner_name || '').toLowerCase().includes(q) ||
        (d.matched_customer_name || '').toLowerCase().includes(q);
      return catOk && mapOk && searchOk;
    });
  }, [devices, catFilter, mapFilter, search]);

  const stats = useMemo(() => {
    let servers = 0, desktops = 0, m365 = 0, mailboxes = 0, mapped = 0, monthly = 0;
    for (const d of devices) {
      const cat = d.billing?.category ?? d.category;
      if (isServer(cat)) servers++;
      else if (cat === 'desktop') desktops++;
      else if (cat === 'm365') { m365++; mailboxes += d.mailbox_count || 0; }
      if (d.matched_customer_id) {
        mapped++;
        monthly += d.billing?.monthly_total || 0;
      }
    }
    return { servers, desktops, m365, mailboxes, mapped, unmapped: devices.length - mapped, monthly };
  }, [devices]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.toLowerCase();
    return customers.filter(c => !q || c.xero_name.toLowerCase().includes(q)).slice(0, 100);
  }, [customers, customerSearch]);

  const unmappedInView = filteredDevices.filter(d => !d.matched_customer_id);
  const allUnmappedSelected = unmappedInView.length > 0 && unmappedInView.every(d => selectedDevices.has(d.id));
  const toggleSelectAll = () => {
    if (allUnmappedSelected) setSelectedDevices(new Set());
    else setSelectedDevices(new Set(unmappedInView.map(d => d.id)));
  };

  const modalQty = mapDevice ? (mapCategory === 'm365' ? (mapDevice.mailbox_count || 0) : 1) : 0;
  const modalTotal = modalQty * (parseFloat(mapPrice) || 0);

  if (loading) {
    return <div className="cove-page"><div className="cove-loading">Loading Cove data…</div></div>;
  }

  return (
    <div className="cove-page">
      <div className="cove-header">
        <div>
          <h1>Cove Data Protection</h1>
          <p className="cove-subtitle">Map backup devices to customers and price them for billing</p>
        </div>
        <div className="cove-header-actions">
          <button className="cove-btn ghost" onClick={() => setShowConfig(true)}>
            {status?.connected ? 'Reconfigure' : 'Configure'}
          </button>
          {status?.connected && (
            <>
              <button className="cove-btn ghost" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
              </button>
              <button className="cove-btn primary" onClick={handleSync} disabled={syncing}>
                {syncing ? '⟳ Syncing…' : '🔗 Auto-Map by Name'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`cove-status ${status?.connected ? 'connected' : 'disconnected'}`}>
        <span className="cove-status-dot" />
        <strong>{status?.connected ? 'Connected' : 'Not connected'}</strong>
        {status?.connected && <span className="cove-status-sub">{devices.length} devices loaded</span>}
        {status?.error && <span className="cove-status-err">{status.error}</span>}
      </div>

      {status?.connected && (
        <>
          {/* Summary */}
          <div className="cove-summary">
            <div className="cove-card"><div className="cove-card-val">{devices.length}</div><div className="cove-card-lbl">Devices</div></div>
            <div className="cove-card"><div className="cove-card-val">{stats.servers}</div><div className="cove-card-lbl">Servers</div></div>
            <div className="cove-card"><div className="cove-card-val">{stats.desktops}</div><div className="cove-card-lbl">Desktops</div></div>
            <div className="cove-card"><div className="cove-card-val">{stats.m365}</div><div className="cove-card-lbl">M365 · {stats.mailboxes} mbx</div></div>
            <div className="cove-card ok"><div className="cove-card-val">{stats.mapped}</div><div className="cove-card-lbl">Mapped</div></div>
            <div className="cove-card warn"><div className="cove-card-val">{stats.unmapped}</div><div className="cove-card-lbl">Unmapped</div></div>
            <div className="cove-card total"><div className="cove-card-val">{rand(stats.monthly)}</div><div className="cove-card-lbl">Billable / month</div></div>
          </div>

          {/* Filters */}
          <div className="cove-toolbar">
            <div className="cove-chip-group">
              {(['all', 'server', 'desktop', 'm365'] as CatFilter[]).map(f => (
                <button key={f} className={catFilter === f ? 'active' : ''} onClick={() => setCatFilter(f)}>
                  {f === 'all' ? 'All types' : f === 'server' ? 'Servers' : f === 'desktop' ? 'Desktops' : 'M365'}
                </button>
              ))}
            </div>
            <div className="cove-chip-group">
              {(['all', 'mapped', 'unmapped'] as MapFilter[]).map(f => (
                <button key={f} className={mapFilter === f ? 'active' : ''} onClick={() => setMapFilter(f)}>
                  {f === 'all' ? 'All' : f === 'mapped' ? `Mapped (${stats.mapped})` : `Unmapped (${stats.unmapped})`}
                </button>
              ))}
            </div>
            <input
              className="cove-search"
              placeholder="Search device, Cove customer, mapped customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {selectedDevices.size > 0 && (
              <button className="cove-btn primary" onClick={() => { setSelectedCustomer(null); setBulkOpen(true); }}>
                Map {selectedDevices.size} selected
              </button>
            )}
          </div>

          {/* Table */}
          <div className="cove-table-wrap">
            <table className="cove-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" checked={allUnmappedSelected} onChange={toggleSelectAll} title="Select all unmapped in view" />
                  </th>
                  <th>Device</th>
                  <th>Cove Customer</th>
                  <th>Type</th>
                  <th>Mapped Customer</th>
                  <th className="cove-num">Unit Price</th>
                  <th className="cove-num">Monthly</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.length === 0 ? (
                  <tr><td colSpan={8} className="cove-empty">No devices match the current filters.</td></tr>
                ) : filteredDevices.map(device => {
                  const cat = device.billing?.category ?? device.category;
                  const b = device.billing;
                  const mapped = !!device.matched_customer_id;
                  return (
                    <tr key={`${device.id}-${device.gone ? 'gone' : 'live'}`} className={`${mapped ? 'mapped' : ''}${device.gone ? ' gone' : ''}`}
                        title={device.gone ? 'This mapped device is no longer in Cove - nothing is billed, its lines are off in Invoice Prep' : undefined}>
                      <td>
                        {!mapped && (
                          <input type="checkbox" checked={selectedDevices.has(device.id)} onChange={() => toggleDevice(device.id)} />
                        )}
                      </td>
                      <td>
                        <div className="cove-dev-name">{device.name}{device.gone && <span className="cove-tag danger" title="Not in Cove any more">gone · not billed</span>}</div>
                        {device.os_name && <div className="cove-dev-os">{device.os_name}</div>}
                      </td>
                      <td className="cove-muted">{device.partner_name || 'Unknown'}</td>
                      <td>
                        {mapped ? (
                          <select
                            className={`cove-cat-select cat-${cat}`}
                            value={cat}
                            onChange={e => handleInlineCategory(device, e.target.value)}
                          >
                            {CATEGORY_ORDER.map(c => (
                              <option key={c} value={c}>{label(c)}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`cove-badge cat-${cat}`}>{SHORT_LABEL[cat] || cat}</span>
                        )}
                        {cat === 'm365' && <span className="cove-mbx">{device.mailbox_count} mbx</span>}
                      </td>
                      <td>
                        {device.matched_customer_name
                          ? <span className="cove-match ok">{device.matched_customer_name}</span>
                          : <span className="cove-match no">Not mapped</span>}
                      </td>
                      <td className="cove-num">
                        {b ? (
                          <span className={mapped ? '' : 'cove-preview'}>
                            {rand(parseFloat(b.unit_price || '0'))}
                            <span className="cove-per">/{b.unit}</span>
                          </span>
                        ) : '–'}
                      </td>
                      <td className="cove-num">
                        {b ? (
                          <strong className={mapped ? 'cove-money' : 'cove-preview'}>{rand(b.monthly_total)}</strong>
                        ) : '–'}
                      </td>
                      <td>
                        {mapped ? (
                          <div className="cove-row-actions">
                            <button className="cove-btn xs" onClick={() => openMapModal(device)}>Edit</button>
                            <button className="cove-btn xs danger" onClick={() => handleUnmap(device.id)}>Unmap</button>
                          </div>
                        ) : (
                          <button className="cove-btn xs primary" onClick={() => openMapModal(device)}>Map</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Config Modal */}
      {showConfig && (
        <div className="cove-modal-overlay" onClick={() => setShowConfig(false)}>
          <div className="cove-modal" onClick={e => e.stopPropagation()}>
            <div className="cove-modal-head"><h3>Cove Configuration</h3><button className="cove-x" onClick={() => setShowConfig(false)}>✕</button></div>
            <div className="cove-modal-body">
              <label className="cove-field"><span>Partner / Company Name</span>
                <input value={config.partner} onChange={e => setConfig({ ...config, partner: e.target.value })} placeholder="Your company name in Cove" /></label>
              <label className="cove-field"><span>API Username (email)</span>
                <input type="email" value={config.username} onChange={e => setConfig({ ...config, username: e.target.value })} placeholder="api-user@yourcompany.com" /></label>
              <label className="cove-field"><span>API Password</span>
                <input type="password" value={config.api_password} onChange={e => setConfig({ ...config, api_password: e.target.value })} placeholder="Cove API password" /></label>
              <label className="cove-field"><span>Your CloudView Password (verification)</span>
                <input type="password" value={config.user_password} onChange={e => setConfig({ ...config, user_password: e.target.value })} placeholder="Enter your CloudView password" /></label>
            </div>
            <div className="cove-modal-foot">
              <button className="cove-btn ghost" onClick={() => setShowConfig(false)}>Cancel</button>
              <button className="cove-btn primary" onClick={handleSaveConfig}>Connect</button>
            </div>
          </div>
        </div>
      )}

      {/* Map / Edit Modal */}
      {mapDevice && (
        <div className="cove-modal-overlay" onClick={() => setMapDevice(null)}>
          <div className="cove-modal" onClick={e => e.stopPropagation()}>
            <div className="cove-modal-head">
              <h3>{mapDevice.matched_customer_id ? 'Edit device billing' : 'Map device to customer'}</h3>
              <button className="cove-x" onClick={() => setMapDevice(null)}>✕</button>
            </div>
            <div className="cove-modal-body">
              <div className="cove-dev-info">
                <strong>{mapDevice.name}</strong>
                <span>{mapDevice.partner_name} · {mapDevice.os_name || 'Unknown OS'}</span>
              </div>

              <label className="cove-field"><span>Search customer</span>
                <input autoFocus placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </label>
              <div className="cove-customer-list">
                {filteredCustomers.map(c => (
                  <div key={c.id} className={`cove-customer ${selectedCustomer === c.id ? 'selected' : ''}`} onClick={() => setSelectedCustomer(c.id)}>
                    {c.xero_name}
                  </div>
                ))}
                {filteredCustomers.length === 0 && <div className="cove-customer empty">No customers found</div>}
              </div>

              <div className="cove-pricing">
                <label className="cove-field"><span>Billing type</span>
                  <select value={mapCategory} onChange={e => changeMapCategory(e.target.value)}>
                    {CATEGORY_ORDER.map(c => <option key={c} value={c}>{label(c)} — {rand(parseFloat(rate(c)))}/{categories[c]?.unit ?? FALLBACK_CATEGORIES[c]?.unit}</option>)}
                  </select>
                </label>
                <div className="cove-price-row">
                  <label className="cove-field price"><span>Unit price (R)</span>
                    <input type="number" min="0" step="0.01" value={mapPrice} onChange={e => setMapPrice(e.target.value)} />
                  </label>
                  <div className="cove-qty">
                    <span>Qty</span>
                    <strong>{modalQty}{mapCategory === 'm365' ? ' mailboxes' : ''}</strong>
                  </div>
                </div>
                <div className="cove-total-row"><span>Total / month</span><strong>{rand(modalTotal)}</strong></div>
              </div>
            </div>
            <div className="cove-modal-foot">
              <button className="cove-btn ghost" onClick={() => setMapDevice(null)}>Cancel</button>
              <button className="cove-btn primary" onClick={handleMap} disabled={!selectedCustomer || saving}>
                {saving ? 'Saving…' : mapDevice.matched_customer_id ? 'Save' : 'Map'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Modal */}
      {bulkOpen && (
        <div className="cove-modal-overlay" onClick={() => setBulkOpen(false)}>
          <div className="cove-modal" onClick={e => e.stopPropagation()}>
            <div className="cove-modal-head"><h3>Map {selectedDevices.size} devices</h3><button className="cove-x" onClick={() => setBulkOpen(false)}>✕</button></div>
            <div className="cove-modal-body">
              <div className="cove-dev-info">
                <strong>{selectedDevices.size} devices selected</strong>
                <span>{devices.filter(d => selectedDevices.has(d.id)).slice(0, 4).map(d => d.name).join(', ')}{selectedDevices.size > 4 ? ` +${selectedDevices.size - 4} more` : ''}</span>
              </div>
              <label className="cove-field"><span>Search customer</span>
                <input autoFocus placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </label>
              <div className="cove-customer-list">
                {filteredCustomers.map(c => (
                  <div key={c.id} className={`cove-customer ${selectedCustomer === c.id ? 'selected' : ''}`} onClick={() => setSelectedCustomer(c.id)}>
                    {c.xero_name}
                  </div>
                ))}
              </div>
              <div className="cove-pricing">
                <label className="cove-field"><span>Billing type (optional — leave as auto to keep each device's detected type)</span>
                  <select value={bulkCategory} onChange={e => { setBulkCategory(e.target.value); setBulkPrice(e.target.value ? rate(e.target.value) : ''); }}>
                    <option value="">Auto (detected per device)</option>
                    {CATEGORY_ORDER.map(c => <option key={c} value={c}>{label(c)} — {rand(parseFloat(rate(c)))}/{categories[c]?.unit ?? FALLBACK_CATEGORIES[c]?.unit}</option>)}
                  </select>
                </label>
                {bulkCategory && (
                  <label className="cove-field price"><span>Unit price (R) for all</span>
                    <input type="number" min="0" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} />
                  </label>
                )}
              </div>
            </div>
            <div className="cove-modal-foot">
              <button className="cove-btn ghost" onClick={() => setBulkOpen(false)}>Cancel</button>
              <button className="cove-btn primary" onClick={handleBulkMap} disabled={!selectedCustomer || saving}>
                {saving ? 'Saving…' : `Map ${selectedDevices.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoveBackups;
